import {TTableColumnFilterExpressionDto} from 'components/@common/widgets/custom-table/table-column-filter/table-column-filter-types';
import {TTableHeaderStructure} from 'components/report-configuration/tabs/report-table-settings/modals/excel-header-structure-loader-modal/excel-header-structure-loader-modal.types';
import {IRegistry} from 'shared/types';
import {TLinkDto} from 'shared/types/links';
import {ILookupEntry} from 'shared/types/lookups';
import {ITableReportColumnSavedFilters} from 'store/slices/table-report-slice/table-report-column-filters/table-report-column-filters-types';

import {IReportConfigurationAttribute, TReportConfigurationDataConverted} from '../report-configuration-slice';
import {
    TReportConfigurationCustomColumnDto,
    IReportConfigurationDataDto,
    IReportConfigurationReportColumnDto,
    IReportConfigurationReportGroupDto,
    IReportConfigurationReportMenuItemDto,
    IReportConfigurationTableHeaderDto,
} from '../report-configuration-slice/report-configuration-dto';
import {ITableReportDownloadDocumentType} from './table-report-download-documents';
import {ITableReportRowCommentDto, ITableReportUploadAttachmentsProgress} from './table-report-row-attachments';
import {ITableReportGlobalSearch, ITableReportLocalSearch} from './table-report-search';

export interface ILoadTableReportPageDataArgs {
    templateCode: string;

    useConfigCache?: boolean;
    isInitialRequest?: boolean;

    dataParams: Omit<ITableReportDataRequestParams, 'templateCode'>;
    configParams: ITableReportConfigRequestParams; // только в обычный конфиг
    commonConfigParams: ITableReportCommonConfigRequestParams; // в оба конфига
    extendedConfigParams: ITableReportExtendedConfigRequestParams; // только в расширенный
    extraDefaultParams?: {[key: string]: any}; // дополнительные дефолтные параметры
}

export interface ITableReportColumnFiltersAndSortData {
    sort: Record<string, {
        order: 'ascend' | 'descend' | undefined;
        multiple: number | undefined;
    } | undefined>;
}

export type TTableReportColumnDropdownSort = ITableReportColumnFiltersAndSortData['sort'][string];

export type TTableReportColumnSortForRequest = {
    order: 'ASC' | 'DESC';
    index?: Exclude<TTableReportColumnDropdownSort, undefined>['multiple'];
    columnName: string;
}

export interface ITableReportConfigRequestParams {
    DOC_ID?: string;
    DRILL_ID?: string;
    pageSize?: number;
    openReport?: boolean;
}

export interface ITableReportExtendedConfigRequestParams {
    DRILL_ID?: undefined;
    openReport?: boolean;
    runReport?: boolean;
    parameters?: TTableReportRequestParameters;
    drillParameters?: ITableReportDrilldownParameter[];
}

export interface ITableReportCommonConfigRequestParams {

}

export type TTableReportRequestParameters = {
    DRILL_ID?: string;
    /**
    * Список выбранных документов для кастомных КС
    */
    CHILD_DOC_IDS_CR?: string;
} & Record<string, any>;

export interface ITableReportDataRequestParams {
    templateCode: string;
    docId: string | null;
    page: number;
    size: number | undefined;
    lookupType: string;
    filters?: TTableColumnFilterExpressionDto;
    sort?: TTableReportColumnSortForRequest[];
    includeCR?: boolean;
    parameters?: TTableReportRequestParameters;
    includeComparison?: boolean;
    includeControlRatioBySettings?: boolean;
    comparedDocId?: string;
    searchValue?: string;
    drillParameters?: ITableReportDrilldownParameter[];
    userReportGroups?: {[reportGroupName: string]: IReportConfigurationReportGroupDto} | null;
    groupAfterUserFilter?: boolean | null;
    addHiddenData?: boolean;
}

export type TTableReportBodyItem = {
    [index: string]: string | number | null | boolean | object | Array<any>;
} & {
    ddUrlDto?: TLinkDto[];
}

export interface ITableReportPage {
    content: TTableReportBodyItem[];
    totalElements: number;
    totalPages: number;
    size?: number;
    number?: number;
}

export enum ETableReportChildDataSourceType {
    PARAMETER = 'PARAMETER',
    FILTER = 'FILTER'
}

export interface ITableReportDrilldownParameter {
    childDataSourceType: string;
    childParameterName: string;
    childFilterName: string;
    childFilterGroupNum: number;
    value: string;
}

export interface ITableReportDrillDown {
    columnName: string;
    templateCode: string;
    templateName: string;
    docId: string;
    docTypeCode: string;
    docTypeName: string;
    section: string;
    subsection: string;
    drillId: string;
    rowIndex: number;
    parameters: ITableReportDrilldownParameter[];
    docTypeDescription?: string;
}

export interface ITableReportData {
    page: ITableReportPage;
    extraInfo: (Omit<ITableReportRowCommentDto, 'files'>)[];
    drilldown: ITableReportDrillDown[];
}

export interface ITableReportMenu extends IReportConfigurationReportMenuItemDto {
    key?: string;
    pageNumber?: number;
    customColumns?: TReportConfigurationCustomColumnDto[];
    isLeaf?: boolean;
}

export type TTableReportExtendedConfiguration = Omit<IReportConfigurationDataDto, 'reportMenu' | 'reportHeaders'> & {
    reportMenu?: ITableReportMenu[];
    reportHeaders?: TTableReportHeaders;
}
export interface ITableReportDocumentUploadInfo {
    docExtensionId?: number;
    docId?: string | null;
    reportRequestData?: ITableReportDataRequestParams;
}

export type TTableReportQueryParameters = Record<string, any>;

interface ICustomColumnCondition {
    columnName: string;
    operator: string;
    value: string;
}
export interface ICustomColumnCase {
    color: string;
    icon: string;
    value: string;
    conditions: ICustomColumnCondition[] | null;
}

export type TTableReportColumn = Partial<IReportConfigurationReportColumnDto> & {
    dataIndex: string;
    permanentlyHidden: boolean;
    key: number;
    title: string;
    cases?: ICustomColumnCase[];
    /**
     * ICON - для столбцов - иконок
     * SWITCH - для значений
     * CR_DD - для столбцов КР "Расхождения" с переходом в дочерний документ
     */
    type?: 'ICON' | 'SWITCH' | 'CR_DD';
    linksProperties?: {
        useTableValueAsTitle?: boolean; // для ссылок (чтобы значение в ячейке стало ссылкой),
    };
} & {
    [key: symbol]: any;
}

export interface ITableReportHeaderItem extends Partial<IReportConfigurationTableHeaderDto> {
    value?: string | null;
}

export type TTableReportHeaders = {[reportColumnName: string]: ITableReportHeaderItem};

export type TTableReportFormattedHeadersItem = ITableReportHeaderItem & {
    key: string;
}

export type TTableReportFormattedHeaders = {
    caption: TTableReportFormattedHeadersItem | undefined;
    name: TTableReportFormattedHeadersItem | undefined;
    others: TTableReportFormattedHeadersItem[] | undefined;
}

export type TTableReportComparisonOptions = {
    comparedToDocId: {id: string} | undefined;
    includeCR: boolean;
    includeComparison: boolean;
    includeControlRatioBySettings: boolean;
    isComparisonFormVisible: boolean;

    noFetchWithComparison: boolean;
    noFetchWithCR: boolean;
    noFetchWithCustomCR: boolean;
}
export interface ITableReportSliceState {
    templateConfig?: TTableReportExtendedConfiguration;
    menu?: ITableReportMenu[];
    tableReportData?: ITableReportData;
    columns?: {[reportColumnName: string]: IReportConfigurationReportColumnDto};
    customColumns?: TReportConfigurationCustomColumnDto[];
    filterConditions?: ILookupEntry[];
    localSearch: ITableReportLocalSearch;
    globalSearch: ITableReportGlobalSearch;
    queryParameters: {
        parameters: TTableReportQueryParameters | undefined;
        defaultParameters: TTableReportQueryParameters | undefined;
    };
    reportDownloadDocuments?: IRegistry<ITableReportDownloadDocumentType>;
    documentUploadInfo?: ITableReportDocumentUploadInfo;
    uploadAttachmentsProgress?: ITableReportUploadAttachmentsProgress;
    tableAttributes?: IReportConfigurationAttribute[];
    reportOptions: {
        filters: any; // TTableColumnFilters, нет поддержки рекурсивных структур
        savedFilters: ITableReportColumnSavedFilters;
        activeMenu?: ITableReportMenu;
        pageSize: number;
        noFetchWithFilters?: boolean;
        noFetchWithSort?: boolean;
        noFetchWithParameters?: boolean;
    } & ITableReportColumnFiltersAndSortData;
    comparisonOptions: TTableReportComparisonOptions;
    userReportGroupsOptions?: TReportConfigurationDataConverted;
    selectedDocIds: string[];
    lastLoadRequestArgs?: ILoadTableReportPageDataArgs;
    linkDtoToColumnMapping?: {[column: string]: string | null};
}

export interface IReportPagination {
    total: number | undefined;
    pageSize: number;
    current: number | undefined;
    onChange: (page: number) => void;
}

export enum ETableReportFilterFieldType {
    INTEGER = 'INTEGER',
    NUMBER = 'NUMBER',
    STRING = 'STRING',
    DATE = 'DATE',
    DATETIME = 'DATETIME',
    BOOLEAN = 'BOOLEAN',
    VALUE_SET = 'VALUESET',
    MULTI_VALUE_SET = 'MULTIVALUESET',
    SQL_VALUE_SET = 'SQLVALUESET',
    SQL_MULTI_VALUE_SET = 'SQLMULTIVALUESET',
    VALUERANGE = 'VALUERANGE',
}

export enum ETableReportFilterFieldDefaultType {
    CONTEXT = 'CONTEXT',
    CONST = 'CONST',
    CURRENT_DATETIME = 'CURRENT_DATETIME',
}

export type TDrillUpChildDocument = {
    reportColumns: Record<string, IReportConfigurationReportColumnDto>;
    excelTableHeaderStructure: TTableHeaderStructure | null;
    data: Record<string, string | number>;
    rowIdentifier: {
        key: string;
        value: string | number;
    };
}

export type TDrillUpParentDocument = {
    docId: string;
    docName: string;
    docType: string;
    createdDate: string;
    createdBy: string;
    templateCode: string;
    message?: string | null;
    children?: TDrillUpChildDocument[];
    filters?: TTableColumnFilterExpressionDto | null;
}

export type TDrillUpTreeResponse = {
    parentDocuments: TDrillUpParentDocument[];
    message: string | null;
}
